
/*
 * https://developerx.atlassian.net/browse/CS-4784
 *
 * Created at : 27 Jan 2022           Updated at : 27 Jan 2022
 * Created by : Risman Abdilah        Updated by : Risman Abdilah
 *
 * Version 2.3.0
 */

import Vue from "vue";
import i18n from "@/i18n";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { Action, Getter, Mutation, State } from "vuex-class";

import toast from "@/functions/toast.function";

interface EducationFormType {
  id: number;
  schoolType: any;
  name: any;
  degree: any;
  major: any;
  nim: string;
  gpa: string;
  isCurrent: boolean;
  start_date: string;
  end_date: string;
}

interface SchoolType {
  label: string;
  translation: string;
  id: number;
}

interface SelectType {
  label: string;
  label_translation: string;
  value: number;
}

@Component
export default class ModalEducation extends Vue {
  /* VUEX */

  /* ACTION */
  @Action("actionLoadSchool", { namespace: "misc" })
  private actionLoadSchool: any;
  @Action("actionGetDegree", { namespace: "misc" })
  private actionGetDegree: any;
  @Action("actionGetMajor", { namespace: "misc" })
  private actionGetMajor: any;
  @Action("actionSelectCandidateById", { namespace: "candidate" })
  private actionSelectCandidateById: any;
  @Action("actionInsertCandidateEducation", { namespace: "candidate" })
  private actionInsertCandidateEducation: any;
  @Action("actionUpdateCandidateEducation", { namespace: "candidate" })
  private actionUpdateCandidateEducation: any;

  /* PROP */
  @Prop({ default: false })
  private isShow: boolean;
  @Prop({ default: false })
  private isEdit: boolean;
  @Prop({ default: null })
  private education: any;

  /* STATE */
  @State("array_school", { namespace: "misc" })
  private array_school: any;
  @State("array_degree", { namespace: "misc" })
  private array_degree: any;
  @State("array_major", { namespace: "misc" })
  private array_major: any;

  /* GETTER */
  @Getter("arraySchoolType", { namespace: "academy" })
  private arraySchoolType: any;

  /* MUTATION */
  @Mutation("mutationSchoolArray", { namespace: 'misc' })
  private mutationSchoolArray: any;
  @Mutation("mutationFilterDegree", { namespace: 'misc' })
  private mutationFilterDegree: any;
  @Mutation("mutationMajorArray", { namespace: 'misc' })
  private mutationMajorArray: any;

  @Watch("isShow", { deep: true, immediate: true })
  private listenModal(showModal: boolean) {
    if (showModal) {
      if (this.education) {
        const {
          id,
          nim,
          gpa,
          start_date,
          end_date,
          school_type,
          schoolName,
          degree,
          major,
        } = this.education;

        this.educationForm.id = id;
        this.educationForm.nim = nim;
        this.educationForm.gpa = gpa;
        this.educationForm.name = schoolName;

        // Set School Type
        if (school_type) {
          this.educationForm.schoolType = school_type;
          this.onInputSchoolType(school_type, false);
        }

        // Set Degree
        const degreeModel: SelectType = {
          label: degree.degree,
          label_translation: degree.translation,
          value: degree.id,
        };
        this.educationForm.degree = degreeModel;
        this.onInputDegree(degreeModel, false);

        // Set Major
        const majorModel: SelectType = {
          label: major.major,
          label_translation: major.translation,
          value: major.id,
        };
        this.educationForm.major = majorModel;

        this.educationForm.start_date = start_date;
        if (!end_date) {
          this.educationForm.isCurrent = true;
        } else {
          this.educationForm.end_date = end_date;
        }
      } else {
        this.educationForm = {
          id: 0,
          schoolType: null,
          name: null,
          degree: null,
          major: null,
          nim: "",
          gpa: "",
          isCurrent: false,
          start_date: "",
          end_date: "",
        };
      }
    }
  }

  /* INITIALIZATION */
  private educationForm: EducationFormType = {
    id: 0,
    schoolType: null,
    name: null,
    degree: null,
    major: null,
    nim: "",
    gpa: "",
    isCurrent: false,
    start_date: "",
    end_date: "",
  };

  constructor() {
    super();
  }

  /* METHOD */

  private onSubmit() {
    if (this.isEdit) {
      if (this.validateEducationForm()) {
        const params = {
          candidateId: this.education.user_id,
          education: this.educationForm,
        }
        this.actionUpdateCandidateEducation({
          params,
          success: () => this.listenerSuccess(this.education.user_id),
        });
      }
    } else {
      if (this.validateEducationForm()) {
        const candidateId = this.$route.params.id;
        const params = {
          candidateId,
          education: this.educationForm,
        }
        this.actionInsertCandidateEducation({
          params,
          success: () => this.listenerSuccess(parseInt(candidateId)),
        });
      }
    }
  }

  private listenerSuccess(userId: number) {
    this.actionSelectCandidateById(userId);
    this.$emit("update:isShow", false);
  }

  private validateEducationForm() {
    if (!this.educationForm.schoolType) {
      toast.warning(i18n.t("warning_school_type"));
      return false;
    }

    if (!this.educationForm.name) {
      toast.warning(i18n.t("warning_school_name"));
      return false;
    }

    if (!this.educationForm.degree) {
      toast.warning(i18n.t("warning_degree"));
      return false;
    }

    if (!this.educationForm.major) {
      toast.warning(i18n.t("warning_major"));
      return false;
    }

    if (!this.educationForm.start_date) {
      toast.warning(i18n.t("warning_start_date"));
      return false;
    }

    if (!this.educationForm.isCurrent) {
      if (!this.educationForm.end_date) {
        toast.warning(i18n.t("warning_end_date"));
        return false;
      }
    }

    return true;
  }

  private onInputSchoolType(schoolType: any, reset = true) {
    if (schoolType) {
      this.actionLoadSchool({
        params: {
          schooltype: schoolType.id,
        },
        success: (response: any) => {
          if (reset) {
            this.educationForm.name = '';
          }
        },
        fail: (response: any) => {
          // TODO
        },
      });
      this.actionGetDegree({
        params: { school_type: schoolType.id },
        success: () => {
          if (reset) {
            this.educationForm.degree = null;
            this.educationForm.major = null;
          }
          this.mutationFilterDegree(this.educationForm);
        },
      });
    } else {
      if (reset) {
        this.educationForm.name = '';
        this.educationForm.degree = null;
        this.educationForm.major = null;
      }
      this.mutationSchoolArray([]);
      this.mutationFilterDegree(this.educationForm);
    }
  }

  private onSearchSchool(value: any, toggleLoading: any) {
    toggleLoading();
    this.actionLoadSchool({
      params: {
        schooltype: this.educationForm.schoolType.id,
        search: value,
      },
      success: (response: any) => {
        toggleLoading();
      },
      fail: (response: any) => {
        toggleLoading();
      },
    });
  }

  private onInputSchoolName(school: any) {
    if (school.hasOwnProperty("name")) {
      this.educationForm.name = school.name;
    } else {
      this.educationForm.name = school;
    }
  }

  private onInputDegree(degree: any, reset = true) {
    if (degree) {
      if (reset) {
        this.educationForm.major = null;
      }
      this.actionGetMajor({
        params: { degree: degree.value },
      });
    } else {
      if (reset) {
        this.educationForm.major = null;
      }
      this.mutationMajorArray([]);
    }
  }

  private onInputMajor(major: any) {
    // TODO
  }

  private getLabel(label: string): string {
    if (this.$i18n.locale === "id") {
      return "translation";
    }
    return label;
  }

  private getDegree(label: string): string {
    if (this.$i18n.locale === "id") {
      return "label_translation";
    }
    return label;
  }

  private getMajor(label: string): string {
    if (this.$i18n.locale === "id") {
      return "label_translation";
    }
    return label;
  }
}
